import React, { FC } from 'react';
import {
  Button,
  Loader,
  NotificationStore,
  Paragraph,
  Spacer,
} from '@wunder-mobility/ui-components';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useTypedDispatch } from '@/core/redux/hooks';
import {
  AccountService,
  useLazyRequestCustomerSignedQuery,
  useRequestOnboardingNextStepQuery,
} from '@/features/account/services/Account.service';
import { AccountCacheTags } from '@/features/account/enums';
import { getIdAustriaStep } from '@/features/account/utils/getIdAustriaStep';
import { PopupBlockedError, useExternalPopup } from '@/features/account/hooks/useExternalPopup';

/**
 * Renders the "Fill with ID Austria" button on the Basic Profile onboarding
 * step. Always queries `next-step`; the button only shows when the backend
 * offers an `external` step with `provider: id_austria`.
 *
 * Clicking opens the external NEWCON page in a popup and dims the app behind a
 * blocking overlay (one window, PayPal-style — no second dialog). NEWCON updates
 * the user via the backend and closes itself; on the user's return we refetch so
 * the form repopulates (completed/partial) or the button disappears (if disallowed).
 */
const IdAustriaStep: FC = () => {
  const dispatch = useTypedDispatch();
  const { data: nextSteps } = useRequestOnboardingNextStepQuery();
  const [requestSigned, { isFetching: isStarting }] = useLazyRequestCustomerSignedQuery();

  const idAustriaStep = getIdAustriaStep(nextSteps);

  const { isOpen, open } = useExternalPopup({
    name: 'idAustria',
    onReturn: () => {
      dispatch(
        AccountService.util.invalidateTags([
          AccountCacheTags.REQUEST_ME_DATA,
          AccountCacheTags.ONBOARDING_NEXT_STEP,
        ]),
      );
    },
  });

  if (!idAustriaStep) return null;

  const handleStart = () => {
    open(async () => {
      const signed = await requestSigned().unwrap();
      // The signed `jwt` (sent as `signed-user-data`) is what NEWCON validates and uses
      // to identify the user; `user-id` is sent for parity but ignored by them.
      if (!signed.jwt) {
        throw new Error('Missing signed JWT in customers/signed response');
      }
      const url = new URL(idAustriaStep.redirectUrl);
      url.searchParams.set('user-id', String(signed.userId));
      url.searchParams.set('signed-user-data', signed.jwt);
      return url.toString();
    }).catch((error) => {
      NotificationStore.addNotification({
        type: 'error',
        title: t({ id: 'general.notification.error', message: 'Error' }),
        content:
          error instanceof PopupBlockedError
            ? t({
                id: 'account.idAustria.popupBlocked',
                message: 'Please allow pop-ups for this site to continue with ID Austria.',
              })
            : t({
                id: 'account.idAustria.startError',
                message: 'Could not start ID Austria. Please try again.',
              }),
      });
    });
  };

  return (
    <>
      <Button
        variation="secondary"
        width="full"
        loading={isStarting}
        disabled={isOpen}
        onClick={handleStart}
      >
        <Trans id="account.idAustria.button">Fill with ID Austria</Trans>
      </Button>
      <Spacer height={24} />

      {isOpen && (
        <Loader cover fixed>
          <Paragraph textColor="text-400">
            <Trans id="account.idAustria.status">
              Continue in the ID Austria window — this page updates automatically when you&apos;re
              done.
            </Trans>
          </Paragraph>
        </Loader>
      )}
    </>
  );
};

export default IdAustriaStep;
